import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { State } from '../../types/state'
import { useRequests } from '../../requests/useRequests'
import { PageBanner } from '../Banners/PageBanner'
import { isMinimumPackage } from '../../utils/package-validator'
import { Package } from '../../enums/package.enum'
import { SideMenu } from '../Header/SiteMenu'
import AppNavBar from '../Header/AppNavBar'

// Interfejs dla danych projektanta
interface Designer {
  id?: number
  name: string
  image: string
  backgroundImage?: string
  shortDescription: string
  description: string
  website: string
  portfolio: string[]
  address?: string
  phoneNumber?: string
  type?: string
  clickStats?: {
    phoneClicks: number
    emailClicks: number
    websiteClicks: number
    addressClicks: number
  }
  // Dodane pola dla obsługi presigned URL i podglądu
  imagePreview?: string
  backgroundImagePreview?: string
  portfolioPreview?: string[]
  types: string[]
}

// Interfejs dla przesłanych obrazów
interface UploadedImage {
  key: string
  presignedUrl: string
}

const DesignerProfileEdit = () => {
  const requests = useRequests()
  const navigate = useNavigate()
  const { username, package: userPackage, packageExpiredAt } = useSelector((state: State) => state.user)
  const [loading, setLoading] = useState<boolean>(true)
  const [saving, setSaving] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [clickStats, setClickStats] = useState<{
    phoneClicks: number
    emailClicks: number
    websiteClicks: number
    addressClicks: number
  } | null>(null)
  const [visitStats, setVisitStats] = useState<number>(0)
  const [designerId, setDesignerId] = useState<string | null>(null)

  // Stan początkowy formularza
  const [formData, setFormData] = useState<Designer>({
    name: '',
    image: '',
    shortDescription: '',
    description: '',
    website: '',
    portfolio: [],
    types: ['interior']
  })

  // Stan do obsługi przesyłania zdjęcia profilowego
  const [imageUploading, setImageUploading] = useState<boolean>(false)

  // Stan do obsługi przesyłania zdjęć do portfolio
  const [portfolioUploading, setPortfolioUploading] = useState<boolean>(false)

  // Stan do obsługi przesyłania zdjęcia w tle
  const [backgroundImageUploading, setBackgroundImageUploading] = useState<boolean>(false)

  useEffect(() => {
    // Sprawdzanie czy użytkownik jest zalogowany
    if (!username) {
      navigate('/')
      return
    }

    const fetchData = async () => {
      try {
        // Sprawdzenie czy użytkownik już ma profil projektanta
        try {
          const userDesignerResponse = await requests.getCurrentUserDesigner()
          if (userDesignerResponse.status === 200 && userDesignerResponse.data) {
            // Pobierz dane projektanta
            const designerData = userDesignerResponse.data

            setFormData(designerData)
            setDesignerId(designerData.id.toString())

            // Pobierz presigned URL-e dla zdjęć projektanta
            try {
              const presignedUrlsResponse = await requests.getDesignerPresignedUrls(designerData.id)

              // Zaktualizuj formData o podpisane URL
              setFormData(prev => ({
                ...prev,
                imagePreview: presignedUrlsResponse.data.imagePresignedUrl,
                backgroundImagePreview: presignedUrlsResponse.data.backgroundImagePresignedUrl,
                portfolioPreview: presignedUrlsResponse.data.portfolioPresignedUrls
              }))
            } catch (error) {
              console.error('Nie udało się pobrać presigned URL dla zdjęć', error)
            }

            // Pobieranie statystyk kliknięć
            try {
              const statsResponse = await requests.getDesignerClickStats(designerData.id.toString())
              setClickStats(statsResponse.data)

              // Pobieranie statystyk odwiedzin
              const visitStatsResponse = await requests.getDesignerVisitStats(designerData.id.toString())
              setVisitStats(visitStatsResponse.data.visitCount)
            } catch (err) {
              console.error('Nie udało się pobrać statystyk', err)
            }

            return // Zakończ funkcję, ponieważ już mamy dane
          }
        } catch (error) {
          // Użytkownik nie ma jeszcze profilu projektanta - to normalna sytuacja
          // więc nie ustawiamy błędu
        }

        // Jeśli nie mamy profilu użytkownika i podano konkretne ID w URL
        if (designerId && designerId !== 'new') {
          // Pobieranie danych projektanta z API przez RequestMaker
          const response = await requests.getDesigner(designerId)
          setFormData(response.data)
          setDesignerId(designerId)

          // Pobierz presigned URL-e dla zdjęć projektanta
          try {
            const presignedUrlsResponse = await requests.getDesignerPresignedUrls(+designerId)

            // Zaktualizuj formData o podpisane URL
            setFormData(prev => ({
              ...prev,
              imagePreview: presignedUrlsResponse.data.imagePresignedUrl,
              backgroundImagePreview: presignedUrlsResponse.data.backgroundImagePresignedUrl,
              portfolioPreview: presignedUrlsResponse.data.portfolioPresignedUrls
            }))
          } catch (error) {
            console.error('Nie udało się pobrać presigned URL dla zdjęć', error)
          }

          // Pobieranie statystyk kliknięć
          try {
            const statsResponse = await requests.getDesignerClickStats(designerId)
            setClickStats(statsResponse.data)

            // Pobieranie statystyk odwiedzin
            const visitStatsResponse = await requests.getDesignerVisitStats(designerId)
            setVisitStats(visitStatsResponse.data.visitCount)
          } catch (err) {
            console.error('Nie udało się pobrać statystyk', err)
          }
        }
      } catch (err) {
        setError('Wystąpił błąd podczas pobierania danych. Spróbuj ponownie.')
        console.error(err)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [username, navigate, requests, designerId])

  // Obsługa zmiany pól formularza
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  // Obsługa zmiany zdjęcia profilowego
  const handleProfileImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if ((e.target.files != null) && e.target.files[0]) {
      try {
        setImageUploading(true)

        // Wyślij plik do serwera, przekazując 'designer' jako typ
        const response = await requests.uploadFile(e.target.files[0], 'designer')

        // Aktualizuj dane formularza nowym kluczem pliku
        setFormData(prev => ({
          ...prev,
          image: response.data.fileKey,
          imagePreview: response.data.presignedUrl // Dodajemy podgląd dla użytkownika
        }))
      } catch (error) {
        setError('Nie udało się przesłać zdjęcia profilowego. Spróbuj ponownie.')
        console.error('Błąd podczas przesyłania zdjęcia:', error)
      } finally {
        setImageUploading(false)
      }
    }
  }

  // Obsługa zmiany zdjęcia w tle
  const handleBackgroundImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if ((e.target.files != null) && e.target.files[0]) {
      try {
        setBackgroundImageUploading(true)

        // Wyślij plik do serwera, przekazując 'designer' jako typ
        const response = await requests.uploadFile(e.target.files[0], 'designer')

        // Aktualizuj dane formularza nowym kluczem pliku
        setFormData(prev => {
          const newData = {
            ...prev,
            backgroundImage: response.data.fileKey,
            backgroundImagePreview: response.data.presignedUrl
          }
          return newData
        })
      } catch (error) {
        setError('Nie udało się przesłać zdjęcia w tle. Spróbuj ponownie.')
        console.error('Błąd podczas przesyłania zdjęcia:', error)
      } finally {
        setBackgroundImageUploading(false)
      }
    }
  }

  // Obsługa dodawania zdjęć do portfolio
  const handlePortfolioImageAdd = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if ((e.target.files != null) && e.target.files.length > 0) {
      try {
        setPortfolioUploading(true)

        const files = Array.from(e.target.files)
        const uploadedImages: UploadedImage[] = []
        const uploadPromises = []

        // Ograniczamy do 20 zdjęć w portfolio
        const filesLimit = Math.min(files.length, 20 - (formData.portfolio?.length || 0))

        for (let i = 0; i < filesLimit; i++) {
          const file = files[i]

          // Tworzymy promise dla każdego przesyłania
          const uploadPromise = requests.uploadFile(file, 'designer')
            .then(response => {
              // Dodajemy informacje o przesłanym pliku
              uploadedImages.push({
                key: response.data.fileKey,
                presignedUrl: response.data.presignedUrl
              })
            })

          uploadPromises.push(uploadPromise)
        }

        // Czekamy na zakończenie wszystkich przesłań
        await Promise.all(uploadPromises)

        // Aktualizujemy dane formularza
        setFormData(prev => {
          const currentPortfolio = prev.portfolio || []
          const currentPortfolioPreview = prev.portfolioPreview || []

          return {
            ...prev,
            portfolio: [
              ...currentPortfolio,
              ...uploadedImages.map(img => img.key)
            ],
            portfolioPreview: [
              ...currentPortfolioPreview,
              ...uploadedImages.map(img => img.presignedUrl)
            ]
          }
        })
      } catch (error) {
        setError('Wystąpił błąd podczas przesyłania zdjęć do portfolio.')
        console.error('Błąd przesyłania portfolio:', error)
      } finally {
        setPortfolioUploading(false)
      }
    }
  }

  // Usuwanie zdjęcia z portfolio
  const handleRemovePortfolioImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      portfolio: prev.portfolio.filter((_, i) => i !== index),
      portfolioPreview: (prev.portfolioPreview != null)
        ? prev.portfolioPreview.filter((_, i) => i !== index)
        : []
    }))
  }

  // Obsługa zapisu formularza
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setSaving(true)

      // Tworzymy nowy obiekt bez pól imagePreview i portfolioPreview
      const { imagePreview, portfolioPreview, backgroundImagePreview, ...formDataToSend } = formData

      let response: any

      if (designerId) {
        // Aktualizacja istniejącego profilu
        response = await requests.updateDesigner(designerId, formDataToSend)
      } else {
        // Tworzenie nowego profilu
        response = await requests.createDesigner(formDataToSend)
      }

      setTimeout(() => {
        navigate(`/designers/${designerId ?? response?.data?.id}`)
      }, 1500)
    } catch (err) {
      setError('Wystąpił błąd podczas zapisywania profilu.')
      console.error('Błąd podczas aktualizacji profilu:', err)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className='text-center pt-100 pb-100'>
        <div className='spinner-border text-primary' role='status'>
          <span className='sr-only'>Ładowanie...</span>
        </div>
      </div>
    )
  }

  return (
    <>
      <AppNavBar
        rightIcons={[]}
      />
      <div style={{ display: 'flex', minHeight: 'calc(100vh - 80px)' }}>
        <SideMenu />
        <section className='designer-edit tw-py-8 md:tw-py-10 lg:tw-py-12' style={{ flex: 1, overflow: 'auto', background: '#f8f9fa' }}>
          <div className='container'>
            {error && (
              <div className='alert alert-danger mb-30' role='alert'>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className='row'>
                <div className='col-sm-12 col-md-4 col-lg-3'>
                  {isMinimumPackage(userPackage, Package.PLUS, packageExpiredAt) && (
                    <>
                      <div className='form-group mb-30'>
                        <label className='form-label'>Logo</label>
                        <div className='profile-image-upload'>
                          <div className='profile-image-preview mb-20'>
                            {formData.imagePreview
                              ? (
                                <img
                                  src={formData.imagePreview}
                                  alt='Podgląd zdjęcia profilowego'
                                  className='img-fluid rounded'
                                  style={{ maxHeight: '150px', display: 'block', margin: '0 auto' }}
                                />
                                )
                              : formData.image
                                ? (
                                  <div className='text-center p-3 bg-light rounded'>
                                    <p>Logo zostało ustawione</p>
                                  </div>
                                  )
                                : (
                                  <div className='text-center p-3 bg-light rounded'>
                                    <p>Brak zdjęcia profilowego</p>
                                  </div>
                                  )}
                          </div>
                          <div className='input-group'>
                            <input
                              type='file'
                              id='profileImage'
                              accept='image/*'
                              className='form-control'
                              onChange={handleProfileImageChange}
                              disabled={imageUploading}
                            />
                            {imageUploading && (
                              <div className='ms-2'>
                                <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' />
                                <span className='ms-1'>Przesyłanie...</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className='form-group mb-30'>
                        <label className='form-label'>Zdjęcie w tle</label>
                        <div className='profile-image-upload'>
                          <div className='profile-image-preview mb-20'>
                            {formData.backgroundImagePreview
                              ? (
                                <img
                                  src={formData.backgroundImagePreview}
                                  alt='Podgląd zdjęcia w tle'
                                  className='img-fluid rounded'
                                  style={{ maxHeight: '150px', display: 'block', margin: '0 auto' }}
                                />
                                )
                              : formData.backgroundImage
                                ? (
                                  <div className='text-center p-3 bg-light rounded'>
                                    <p>Zdjęcie w tle zostało ustawione</p>
                                  </div>
                                  )
                                : (
                                  <div className='text-center p-3 bg-light rounded'>
                                    <p>Brak zdjęcia w tle</p>
                                  </div>
                                  )}
                          </div>
                          <div className='input-group'>
                            <input
                              type='file'
                              id='backgroundImage'
                              accept='image/*'
                              className='form-control'
                              onChange={handleBackgroundImageChange}
                              disabled={backgroundImageUploading}
                            />
                            {backgroundImageUploading && (
                              <div className='ms-2'>
                                <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' />
                                <span className='ms-1'>Przesyłanie...</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  <div className='form-group mb-30'>
                    <label htmlFor='website' className='form-label'>Strona internetowa</label>
                    <input
                      type='text'
                      id='website'
                      name='website'
                      className='form-control'
                      placeholder='np. www.twojafirma.pl'
                      value={formData.website}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Statystyki kliknięć i odwiedzin */}
                  {designerId && (
                    <div className='contact-stats mt-30 mb-30'>
                      <h5 className='mb-3'>Statystyki wizytówki</h5>

                      {/* Odwiedziny profilu - nowa sekcja */}
                      <div className='mb-3'>
                        <div className='card'>
                          <div className='card-body'>
                            <h6 className='card-title text-center'>Łączna liczba odwiedzin</h6>
                            <p className='card-text text-center h3'>{visitStats}</p>
                          </div>
                        </div>
                      </div>

                      <h6 className='mb-2'>Wyświetlenia kontaktu</h6>
                      <ul className='list-group'>
                        <li className='list-group-item d-flex justify-content-between align-items-center'>
                          Telefon
                          <span className='badge badge-primary badge-pill'>{clickStats?.phoneClicks || 0}</span>
                        </li>
                        <li className='list-group-item d-flex justify-content-between align-items-center'>
                          Email
                          <span className='badge badge-primary badge-pill'>{clickStats?.emailClicks || 0}</span>
                        </li>
                        <li className='list-group-item d-flex justify-content-between align-items-center'>
                          Strona WWW
                          <span className='badge badge-primary badge-pill'>{clickStats?.websiteClicks || 0}</span>
                        </li>
                        <li className='list-group-item d-flex justify-content-between align-items-center'>
                          Adres
                          <span className='badge badge-primary badge-pill'>{clickStats?.addressClicks || 0}</span>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>

                <div className='col-sm-12 col-md-8 col-lg-9'>
                  <div className='row'>
                    <div className='col-sm-12 col-md-6 mb-30'>
                      <label htmlFor='name' className='form-label'>Imię i nazwisko/Nazwa firmy</label>
                      <input
                        type='text'
                        id='name'
                        name='name'
                        className='form-control'
                        placeholder='Twoje imię i nazwisko'
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className='col-sm-12 col-md-6 mb-30'>
                      <label className='form-label'>Branże</label>
                      <div>
                        {[
                          { value: 'interior_design', label: 'Projektowanie wnętrz' },
                          { value: 'construction_design', label: 'Projektowanie konstrukcji' },
                          { value: 'garden_design', label: 'Projektowanie ogrodów' },
                          { value: 'design', label: 'Projektowanie' },
                          { value: 'other_services', label: 'Pozostałe usługi' }
                        ].map(option => (
                          <div key={option.value} className='form-check'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              id={`type-${option.value}`}
                              value={option.value}
                              checked={formData.types.includes(option.value)}
                              onChange={e => {
                                const checked = e.target.checked
                                setFormData(prev => {
                                  let newTypes = prev.types || []
                                  if (checked) {
                                    newTypes = [...newTypes, option.value]
                                  } else {
                                    newTypes = newTypes.filter(t => t !== option.value)
                                  }
                                  return { ...prev, types: newTypes }
                                })
                              }}
                            />
                            <label className='form-check-label' htmlFor={`type-${option.value}`}>
                              {option.label}
                            </label>
                          </div>
                        ))}
                      </div>
                      <small className='form-text text-muted'>
                        Zaznacz dowolną liczbę branż, które Cię opisują
                      </small>
                    </div>
                  </div>

                  <div className='form-group mb-30'>
                    <label htmlFor='shortDescription' className='form-label'>Krótki opis (specjalizacja)</label>
                    <input
                      type='text'
                      id='shortDescription'
                      name='shortDescription'
                      className='form-control'
                      placeholder='np. Projektant wnętrz z 10-letnim doświadczeniem'
                      value={formData.shortDescription}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className='form-group mb-30'>
                    <label htmlFor='description' className='form-label'>Szczegółowy opis</label>
                    <textarea
                      id='description'
                      name='description'
                      className='form-control'
                      rows={5}
                      placeholder='Opisz swoją specjalizację, doświadczenie i podejście do projektowania...'
                      value={formData.description}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className='form-group mb-30'>
                    <label className='form-label'>Portfolio (max 20 zdjęć)</label>

                    <div className='portfolio-images mb-20'>
                      <div className='row'>
                        {(formData.portfolioPreview != null) && formData.portfolioPreview.map((image, index) => (
                          image && (
                            <div key={index} className='col-sm-6 col-md-4 col-lg-3 mb-20'>
                              <div className='portfolio-image-item position-relative'>
                                <img
                                  src={image}
                                  alt={`Projekt ${index + 1}`}
                                  className='img-fluid rounded'
                                  style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                                />
                                <button
                                  type='button'
                                  className='tw-absolute tw-top-2 tw-right-2 tw-bg-red-600 hover:tw-bg-red-700 focus:tw-bg-red-700 tw-text-white tw-p-3 tw-text-xs tw-opacity-90 tw-rounded tw-w-[10px] tw-h-[26px] tw-flex tw-items-center tw-justify-center tw-border-none tw-shadow-[0_2px_4px_rgba(0,0,0,0.2)]'
                                  onClick={() => handleRemovePortfolioImage(index)}
                                  title='Usuń zdjęcie'
                                >
                                  <i className='fas fa-trash tw-text-sm' />
                                </button>
                              </div>
                            </div>
                          )
                        ))}
                        {((formData.portfolioPreview == null) || formData.portfolioPreview.length === 0) && formData.portfolio && formData.portfolio.length > 0 && (
                          <div className='col-12 mb-20'>
                            <div className='text-center p-3 bg-light rounded'>
                              <p>Portfolio zawiera {formData.portfolio.length} zdjęć, ale podgląd nie jest dostępny</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className='input-group'>
                      <input
                        type='file'
                        id='portfolioImages'
                        accept='image/*'
                        multiple
                        className='form-control'
                        onChange={handlePortfolioImageAdd}
                        disabled={portfolioUploading || (formData.portfolio?.length || 0) >= 20}
                      />
                      <label className='input-group-text' htmlFor='portfolioImages'>
                        Dodaj zdjęcia
                      </label>
                      {portfolioUploading && (
                        <div className='ms-2'>
                          <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' />
                          <span className='ms-1'>Przesyłanie...</span>
                        </div>
                      )}
                    </div>
                    <small className='form-text text-muted mt-1'>
                      Możesz dodać maksymalnie 20 zdjęć do swojego portfolio.
                      {formData.portfolio && <span> Aktualnie masz {formData.portfolio.length}.</span>}
                    </small>
                  </div>

                  <div className='form-group mt-40'>
                    <button
                      type='submit'
                      className={`
                  tw-mr-4 tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                  tw-text-white
                  tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                `}
                      disabled={saving}
                    >
                      {saving
                        ? (
                          <>
                            <span
                              className='tw-inline-block tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-2 tw-border-solid tw-border-current tw-border-r-transparent tw-mr-2'
                              role='status'
                              aria-hidden='true'
                            />
                            Zapisywanie...
                          </>
                          )
                        : (
                            'Zapisz profil'
                          )}
                    </button>

                    <button
                      type='button'
                      className={`
                                      tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                                      tw-text-white
                                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-red-500 hover:tw-bg-opacity-90 focus:tw-bg-red-500
                                    `}
                      onClick={() => navigate(-1)}
                      disabled={saving}
                    >
                      Anuluj
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </section>
      </div>
    </>
  )
}

export default DesignerProfileEdit
